import { Controller } from '@hotwired/stimulus'
import { initLocationMap, renderMapLocations, updateMapLocations, recenterMapToLocations } from '../utils/map'
import { get } from "@rails/request.js";

const CHUNK_SIZE = 25;
const DEFAULT_MAP_ZOOM = 9;

export default class extends Controller {
  static targets = ['showAssetToggler', 'sideBar', 'locationList', 'resultsCount'];
  static values = { targetId: String };

  pageNumber = 0;
  searchParams = {};
  locationsList = [];

  async connect () {
    this.map = initLocationMap(this.targetIdValue, window.mapbox_data.coordinates, DEFAULT_MAP_ZOOM, true)
    await this.renderMap()
    
    if (typeof window.__mapUpdating === 'undefined'){
      window.__mapUpdating = false;
    }
    if (typeof window.__mapRecentering === 'undefined'){
      window.__mapRecentering = false;
    }

    this.map.on('moveend', () => {
      if (window.__mapUpdating || window.__mapRecentering) {
        return;
      }
      this.updateMap();
    });

    this.debouncedScroll = this.debounce(this.scrolled.bind(this), 200);
    this.sideBarTarget.addEventListener('scroll', this.debouncedScroll);
    }

 disconnect() {
    if (this.debouncedScroll) {
      this.sideBarTarget.removeEventListener('scroll', this.debouncedScroll);
    }
  }

  bounds() {
    const {
      _ne: { lat: latitude_1, lng: longitude_1 },
      _sw: { lat: latitude_2, lng: longitude_2 }
    } = this.map.getBounds();

    return {
      coordinates: true,
      latitude_1,
      longitude_1,
      latitude_2,
      longitude_2,
    };
  }

  scrolled() {
    const { bottom } = this.locationListTarget.getBoundingClientRect();

    if (this.sideBarTarget.clientHeight + this.sideBarTarget.offsetTop > bottom ) {
      this.pageNumber += 1
      const locations = this.locationsChunk()
      this.loadLocations(locations, true);
    }
  }

  updateResultsCount(count) {
    if (this.hasResultsCountTarget) {
      this.resultsCountTarget.textContent = `${count} result${count !== 1 ? 's' : ''}`;
    }
  }

  async renderMap() {
    const res = await this.fetchLocations();
    this.locationsList = res.locations;
    this.updateResultsCount(this.locationsList.length);

    const locations = this.locationsChunk();
    this.loadLocations(locations);
    renderMapLocations(this.map, this.locationsList);
  }

  locationsChunk() {
    const initialValue = this.pageNumber * CHUNK_SIZE
    return this.locationsList.slice(initialValue, initialValue + CHUNK_SIZE)
  }
  
  async updateMap() {
    if (window.__mapUpdating) {
      return;
    }

    window.__mapUpdating = true;
    try {
      this.searchParams = window.searchParams ?? {};
      const res = await this.fetchLocations();
      this.locationsList = res.locations;
      this.updateResultsCount(this.locationsList.length);

      const locations = this.locationsChunk();
      this.loadLocations(locations);
      updateMapLocations(this.map, this.locationsList);

      if (this.searchParams["search_attributes"] && this.locationsList.length > 0) {
        window.__mapRecentering = true;
        recenterMapToLocations(this.map, this.locationsList);
        this.map.once('moveend', () => {
          window.__mapRecentering = false;
        });
      }

    } finally {
      window.__mapUpdating = false;
    }
  }

  loadLocations(locations, append=false) {
    if (!append) {
      this.pageNumber = 0
      this.locationListTarget.innerHTML = ""
    }

    const showAsset = this.hasShowAssetTogglerTarget ? this.showAssetTogglerTarget.checked : false;

    locations.forEach( location => {
      const cardElement = this.createLocationCard(location, showAsset)
      this.locationListTarget.appendChild(cardElement)
    })
  }

  createLocationCard(location, showAsset) {
    const cardElement = document.createElement('div')
    cardElement.setAttribute('data-controller', 'location-card')
    cardElement.setAttribute('data-location-card-location-value', JSON.stringify(location))
    cardElement.setAttribute('data-location-card-show-asset-value', showAsset)
    return cardElement
  }

  updateShowAssets() {
    const showAsset = this.showAssetTogglerTarget.checked
    const locationCards = this.locationListTarget.querySelectorAll('[data-controller*="location-card"]')
    locationCards.forEach(card => {
      const controller = this.application.getControllerForElementAndIdentifier(card, 'location-card')
      if (controller) {
        controller.showAssetValue = showAsset
      }
    })
  }

  async fetchLocations() {
    this.sideBarTarget.scrollTo(0, 0)

    const params = { ...this.searchParams }

    if (!params["search_attributes"] || params["search_attributes"].trim() === "") {
      Object.assign(params, this.bounds())
    }

    const response = await get("/location_management/api/v1/locations", {
      query: params
    });

    return await response.json;
  }

  debounce(fn, delay) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => fn(...args), delay)
    }
  }
}
